import React from 'react';
import {
  Grid,
  TextField,
  Typography,
  Box,
  Autocomplete,
  Checkbox,
  Radio,
  Button,
  FormControlLabel,
  RadioGroup,
} from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import FieldGroup from '../FieldGroup';

// Section title with optional inline controls on the right
function SectionTitle({ children, right }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        mb: 1.5,
        flexWrap: 'wrap',
      }}
    >
      <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#4a3f2f' }}>
        {children}
      </Typography>
      {right}
    </Box>
  );
}

const cardSx = {
  border: '1px solid #ece7df',
  borderRadius: '10px',
  p: 2.5,
  height: '100%',
  backgroundColor: '#fff',
};

export default function PropertyStep({ form }) {
  const {
    state,
    setField,
    setSameAsHome,
    setDropPinUrl,
    setJobType,
    setSpecies,
    forestInfoIpFields,
    forestInfoSectionDropDownData,
    jobTypeIpFields,
    speciesIpFields,
  } = form;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* ── Site address ──────────────────────────────────────── */}
      <FieldGroup title="Site address">
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={state.sameAsHome}
                onChange={e => setSameAsHome(e.target.checked)}
              />
            }
            label="Same as home address"
          />
        </Grid>

        {!state.sameAsHome && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField size="small" fullWidth label="Address Line 1" value={state.siteAddressLine1}
                onChange={e => setField('siteAddressLine1', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" fullWidth label="Address Line 2" value={state.siteAddressLine2}
                onChange={e => setField('siteAddressLine2', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                freeSolo
                options={forestInfoSectionDropDownData?.siteCounty || []}
                value={state.siteTown || null}
                onChange={(e, v) => setField('siteTown', v || '')}
                onInputChange={(e, v) => setField('siteTown', v)}
                renderInput={p => <TextField {...p} label="County" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" fullWidth label="Eircode" value={state.siteZip}
                onChange={e => setField('siteZip', e.target.value)} />
            </Grid>
          </>
        )}

        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            size="small"
            label="Forest Location (paste Google Maps link)"
            placeholder="Paste the full Google Maps link here"
            helperText={
              state.dropPinLat && state.dropPinLng
                ? `Detected: ${state.dropPinLat}, ${state.dropPinLng}`
                : 'Use the full link (not a shortened maps.app.goo.gl link)'
            }
            value={state.dropPinUrl}
            onChange={e => setDropPinUrl(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'flex-start', mt: 0.5 }}>
          <Button variant="outlined" size="medium" startIcon={<MapIcon />} href="https://www.google.com/maps" target="_blank" rel="noopener">
            Open Google Maps
          </Button>
        </Grid>
      </FieldGroup>

      {/* ── Managed by forester + Felling licence (side-by-side cards) ── */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box sx={cardSx}>
            <SectionTitle
              right={
                <RadioGroup
                  row
                  value={state.isForesterManaging === true ? 'yes' : state.isForesterManaging === false ? 'no' : ''}
                  onChange={e => setField('isForesterManaging', e.target.value === 'yes')}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              }
            >
              Managed by a forester?
            </SectionTitle>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {state.isForesterManaging && (
                <TextField size="small" fullWidth label="Forester Name" value={state.foresterName}
                  onChange={e => setField('foresterName', e.target.value)} />
              )}
              <TextField size="small" fullWidth label="Enquiry Originated From" value={state.enquiryOriginatedFrom}
                onChange={e => setField('enquiryOriginatedFrom', e.target.value)} />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={cardSx}>
            <SectionTitle
              right={
                <RadioGroup
                  row
                  value={state.hasFellingLicense === true ? 'yes' : state.hasFellingLicense === false ? 'no' : ''}
                  onChange={e => setField('hasFellingLicense', e.target.value === 'yes')}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              }
            >
              Have a felling licence?
            </SectionTitle>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {state.hasFellingLicense && (
                <TextField size="small" fullWidth label="Felling Licence Number" value={state.fellingLicenseNumber}
                  onChange={e => setField('fellingLicenseNumber', e.target.value)} />
              )}
              {!state.hasFellingLicense && (
                <Typography sx={{ fontSize: '0.85rem', color: '#8a8375' }}>
                  No licence on file — you can add the number later once it's issued.
                </Typography>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* ── Description of forest ─────────────────────────────── */}
      <FieldGroup title="Description of forest">
        <Grid item xs={12} sm={6} md={4}>
          <TextField size="small" fullWidth label="Size In Hectares" value={state.sizeInHectares}
            onChange={e => setField('sizeInHectares', e.target.value)} />
        </Grid>
        {forestInfoIpFields.map((f, i) => (
          <Grid item xs={12} sm={6} md={f.width || 4} key={`${f.name}_${i}`}>
            {f.typeOfInp === 'select' ? (
              <Autocomplete
                size="small"
                options={forestInfoSectionDropDownData?.[f.name] || []}
                value={state[f.name] ?? null}
                onChange={(e, v) => setField(f.name, v)}
                renderInput={p => <TextField {...p} label={f.label} />}
              />
            ) : (
              <TextField
                size="small"
                fullWidth
                type={f.textFieldType || 'text'}
                label={f.label}
                value={state[f.name] ?? ''}
                onChange={e => setField(f.name, e.target.value)}
              />
            )}
          </Grid>
        ))}
        <Grid item xs={12} sm={6}>
          <TextField size="small" fullWidth label="Forestry Licence Viewer" value={state.forestryLicenceViewer}
            onChange={e => setField('forestryLicenceViewer', e.target.value)} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField size="small" fullWidth label="Land Direct Viewer" value={state.landDirectViewer}
            onChange={e => setField('landDirectViewer', e.target.value)} />
        </Grid>
      </FieldGroup>

      {/* ── Job Type ──────────────────────────────────────────── */}
      <Box sx={cardSx}>
        <SectionTitle>Job Type</SectionTitle>
        <Grid container spacing={1.5}>
          {jobTypeIpFields.map((f, i) => (
            <Grid item xs={6} sm={4} md={3} key={`${f.name}_${i}`}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={state.jobType[f.name] || false}
                    onChange={e => setJobType(f.name, e.target.checked)}
                  />
                }
                label={f.label}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ── Species ───────────────────────────────────────────── */}
      <Box sx={cardSx}>
        <SectionTitle>Species</SectionTitle>
        <Grid container spacing={1.5}>
          {speciesIpFields.map((f, i) => (
            <Grid item xs={6} sm={4} md={3} key={`${f.name}_${i}`}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={state.species[f.name] || false}
                    onChange={e => setSpecies(f.name, e.target.checked)}
                  />
                }
                label={f.label}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}